import React, { forwardRef, useImperativeHandle } from "react";
import { ProcessedTextDisplay } from "./ProcessedTextDisplay";
import { ProcessedTextEditor } from "./ProcessedTextEditor";
import { useEditableProcessedText } from "@/hooks/useEditableProcessedText";
import { useEditingState } from "@/contexts/EditingStateContext";
import { cn } from "@/lib/utils";

export interface EditableProcessedTextRef {
  forceSave: () => Promise<void>;
}

interface EditableProcessedTextProps {
  processedText: string | null;
  recordId: string;
  onUpdate?: (newText: string) => void;
  readOnly?: boolean;
  className?: string;
  onEditingStateChange?: (isEditing: boolean, hasChanges: boolean) => void;
}

export const EditableProcessedText = forwardRef<EditableProcessedTextRef, EditableProcessedTextProps>((
  {
    processedText,
    recordId,
    onUpdate,
    readOnly = false,
    className = "",
    onEditingStateChange,
  },
  ref
) => {
  const { setIsEditing: setGlobalIsEditing } = useEditingState();

  const {
    isEditing,
    isSaving,
    error,
    editingValue,
    startEditing,
    cancelEditing,
    saveValue,
    updateEditingValue,
    hasChanges,
  } = useEditableProcessedText({
    initialValue: processedText || "",
    recordId,
    onSuccess: onUpdate,
    enabled: !readOnly,
  });

  // Синхронизируем локальное состояние редактирования с глобальным
  React.useEffect(() => {
    setGlobalIsEditing(isEditing);
  }, [isEditing, setGlobalIsEditing]);

  // Уведомляем родительский компонент об изменении состояния редактирования
  React.useEffect(() => {
    onEditingStateChange?.(isEditing, hasChanges);
  }, [isEditing, hasChanges, onEditingStateChange]);

  // Предоставляем API для принудительного сохранения
  useImperativeHandle(ref, () => ({
    forceSave: async () => {
      if (isEditing && hasChanges) {
        await saveValue(editingValue);
      }
    },
  }), [isEditing, hasChanges, editingValue, saveValue]);

  const handleSave = (value: string) => {
    saveValue(value);
  };

  if (isEditing) {
    return (
      <div className={cn("w-full", className)}>
        <ProcessedTextEditor
          initialValue={editingValue}
          onSave={handleSave}
          onCancel={cancelEditing}
          isSaving={isSaving}
          error={error}
        />
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      <ProcessedTextDisplay
        processedText={processedText}
        onEditStart={startEditing}
        isEditable={!readOnly}
      />
    </div>
  );
});

EditableProcessedText.displayName = 'EditableProcessedText';